import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, injectIntl, intlShape } from 'react-intl';
import { get, isEmpty, filter } from 'lodash';
import classnames from 'classnames';
import ReviewSpaceRentalTable from '../elements-tables/review-space-rental-table';
import { CTAButton } from 'shared-components/buttons/cta-button';
import PriceTable from '../elements-tables/price-table';
import TextButton from '../elements-buttons/text-button';
import AddOnsTable from '../app-main/components/reduxForms/fieldSets/AddOnsTable/add-ons-table';
import ModalManager from '../elements-modal/modal-manager-v2';
import { DeclineBkgRequestModal } from './modals/DeclineBkgRequestModal';
import TrustAndSafetyWarning from './TrustAndSafetyWarning';
import SharedIntlProviderHOC from 'shared-components/hocs/sharedIntlProviderHOC';
import { BookingSummary } from '../elements-summaries/booking-summary/BookingSummary';
import { TosCTAdisclaimer } from 'shared-components/terms/cta-disclaimer';

const ReviewRequestMain = ({
  booking = {},
  cleaningFee = '',
  currency = '',
  masterContract = {},
  activityDetailBooking = {},
  spaceRentalSubtotal = 0,
  spaceRentalTotal = 0,
  itemsArray = [],
  addOnsTotal = 0,
  subtotal = 0,
  processingFee = 0,
  total = 0,
  iosWebView,
  toggleModal,
  isHost,
  ssoId = '',
  whichModal = '',
  closeIOSModal = null,
  declineBookingRequest = null,
  acceptBookingRequest = null,
  createCustomOffer = null,
  redirectToInboxDetails = null,
  errorText = '',
  isAcceptDisabled = false,
  isAcceptSpinning = false,
  bookingId = '',
  finalCb = null,
  scrollTopIOS = null,
  spaceRentalObj = {},
  hostAddOnsSummary = {},
  intl,
  profile
}) => {
  const hostAddOns = filter(itemsArray, (addOn) => {
    return !addOn.meta.is_concierge;
  });

  const displayAddOns = get(
    masterContract,
    'calculations.concierge_add_ons_summary'
  )
    ? get(masterContract, 'calculations.host_add_ons_summary.amount', 0)
    : addOnsTotal;

  const displaySplitPayments = get(
    masterContract,
    'split_payment_checkout',
    false
  );
  const guestAmounts = get(masterContract, 'calculations.guest', {});
  const splitPaymentFee = get(guestAmounts, 'split_payment.fee', 0);
  const splitPaymentChargeDate = get(
    masterContract,
    'split_payment_charge_date',
    ''
  );
  const guestSplitPayment = get(guestAmounts, 'split_payment.parts[0]', 0);
  const guestSplitPaymentWithoutFee = get(
    guestAmounts,
    'split_payment.parts[1]',
    0
  );
  const guestSplitPaymentTotal = get(guestAmounts, 'split_payment.total', 0);
  const bookingCreatedDate = get(booking, 'date_created', '');

  const riskObj = get(spaceRentalObj, 'meta.risk', {});
  const showTSWarning = riskObj.host_social_events_policy_agreement_required;
  const [tsWarningAccepted, setTsWarningAccepted] = useState(false);

  return (
    <SharedIntlProviderHOC>
      <div className='ReviewRequestMain ps-row'>
        <ModalManager whichModal={whichModal} toggleModal={toggleModal}>
          <DeclineBkgRequestModal
            id='declineBkgRequestModal'
            bookingId={bookingId}
            booking={activityDetailBooking}
            createCustomOffer={createCustomOffer}
            iosWebView={iosWebView}
            redirectToInboxDetails={redirectToInboxDetails}
            closeIOSModal={closeIOSModal}
            finalCb={finalCb}
            scrollTopIOS={scrollTopIOS}
          />
        </ModalManager>
        <div className="col-xs-12">
          <TextButton
            id='back-to-messages-button'
            buttonText={intl.formatMessage({
              id: 'inbox.review_request_main.message',
              defaultMessage: 'Message'
            })}
            icon={<div className='ico ico-sm icon-back' />}
            iconPosition='left'
            classNames='margin-lg-top'
            onClick={closeIOSModal || redirectToInboxDetails}
          />
        </div>
        <div
          className={classnames('flex space-between middle header col-xs-12', {
            iosWebView
          })}
        >
          <h3>
            <FormattedMessage
              id='inbox.review_request_main.booking_details'
              defaultMessage='Booking Details'
            />
          </h3>
        </div>
        <div className='col-md-8'>
          <ReviewSpaceRentalTable
            discounts={booking.discounts}
            cleaningFee={cleaningFee || 0}
            currency={currency}
            subtotal={spaceRentalSubtotal}
            masterContract={masterContract}
            total={spaceRentalTotal}
          />

          {!isEmpty(hostAddOns) && (
            <AddOnsTable
              masterContract={masterContract}
              itemsArray={itemsArray}
              hostAddOnsSummary={hostAddOnsSummary}
              ssoId={ssoId}
              toggleModal={toggleModal}
              whichModal={whichModal}
              currency={currency}
              isReviewPage
            />
          )}
          <PriceTable
            currency={currency}
            spaceRentalTotal={spaceRentalTotal}
            addOnsTotal={addOnsTotal}
            hostAddOnsSummary={hostAddOnsSummary}
            total={subtotal}
            processingFee={processingFee}
            priceTotal={total}
            isHost={isHost}
            discounts={booking.discounts}
            itemsArray={itemsArray}
            displayAddOns={displayAddOns}
            splitPaymentChargeDate={splitPaymentChargeDate}
            splitPaymentFee={splitPaymentFee}
            splitPaymentAmount={guestSplitPayment}
            splitPaymentAmountWithoutFee={guestSplitPaymentWithoutFee}
            splitPaymentTotal={guestSplitPaymentTotal}
            displaySplitPayments={displaySplitPayments}
            bookingCreatedDate={bookingCreatedDate}
            masterContract={masterContract}
          />

          {showTSWarning && (
            <div className="ts-warning-wrap">
              <TrustAndSafetyWarning
                isAccepted={tsWarningAccepted}
                setIsAccepted={setTsWarningAccepted}
              />
            </div>
          )}

          <div className='help-block text-danger text-right'>{errorText}</div>
          <div className='cta-buttons flex space-between margin-xl-top'>
            <CTAButton
              onClickAction={declineBookingRequest}
              buttonText={intl.formatMessage({
                id: 'inbox.review_request_main.decline',
                defaultMessage: 'Decline'
              })}
              classNameString='btn btn-default'
              isDisabled={false}
            />
            <CTAButton
              onClickAction={createCustomOffer}
              buttonText={intl.formatMessage({
                id: 'inbox.review_request_main.create_offer_text',
                defaultMessage: 'Create offer'
              })}
              classNameString='btn btn-default create-offer-cta margin-lg-right'
              isDisabled={false}
            />
            <CTAButton
              onClickAction={() => acceptBookingRequest(tsWarningAccepted)}
              buttonText={intl.formatMessage({
                id: 'inbox.review_request_main.accept',
                defaultMessage: 'Accept booking'
              })}
              classNameString='btn btn-primary'
              isDisabled={
                isAcceptDisabled || !!(showTSWarning && !tsWarningAccepted)
              }
              spinning={isAcceptSpinning}
            />
          </div>

          <div style={{ paddingTop: '8px' }}>
            <TosCTAdisclaimer
              location='acceptBkg'
              buttonLabel={intl.formatMessage({
                id: 'inbox.review_request_main.accept',
                defaultMessage: 'Accept booking'
              })}
            />
          </div>
        </div>
        <div className='activity-details col-md-4'>
          <BookingSummary
            booking={activityDetailBooking}
            profile={profile}
            intl={intl}
            iosWebViewConfig={{
              isWebView: iosWebView
            }}
          />
        </div>
      </div>
    </SharedIntlProviderHOC>
  );
};

ReviewRequestMain.propTypes = {
  booking: PropTypes.object,
  subtotal: PropTypes.number,
  cleaningFee: PropTypes.number,
  currency: PropTypes.string,
  masterContract: PropTypes.object,
  activityDetailBooking: PropTypes.object,
  spaceRentalTotal: PropTypes.number,
  processingFee: PropTypes.number,
  iosWebView: PropTypes.bool,
  toggleModal: PropTypes.func,
  isHost: PropTypes.bool,
  ssoId: PropTypes.string,
  whichModal: PropTypes.string,
  total: PropTypes.number,
  closeIOSModal: PropTypes.func,
  declineBookingRequest: PropTypes.func,
  acceptBookingRequest: PropTypes.func,
  createCustomOffer: PropTypes.func,
  redirectToInboxDetails: PropTypes.func,
  spaceRentalSubtotal: PropTypes.number,
  itemsArray: PropTypes.array,
  addOnsTotal: PropTypes.number,
  errorText: PropTypes.string,
  isAcceptSpinning: PropTypes.bool,
  isAcceptDisabled: PropTypes.bool,
  bookingId: PropTypes.string,
  finalCb: PropTypes.func,
  scrollTopIOS: PropTypes.func,
  spaceRentalObj: PropTypes.object,
  intl: intlShape
};

export default injectIntl(ReviewRequestMain);
